/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Run-scoped accumulator for chat attachments (inline charts embedded in a chat
// turn — the "chat as lab report" flow). Mirrors clearAgentInvestigations /
// captureAgentInvestigations: a tool pushes an artifact DURING the agent run,
// the chat hook clears the slot before the run and captures it after to spread
// onto the ChatMessageInterface. The durable copy then lives on the message and
// round-trips on save/reload; this store field is ephemeral scratch.

import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { useStore } from '../stores/common.ts';
import { ChatAttachment, SweepChartAttachment } from '../types.ts';
import { canOverlay, overlaySweeps } from './sweepOverlay.ts';

// The most recent sweep chart already in the transcript (or pushed earlier in this
// same turn) that `next` could overlay, newest first. Used to overlay a repeated
// sweep on one set of axes. Charts that cannot share axes with `next` are
// skipped rather than ending the search: a ramp draws a companion chart (the
// MSD exponent beside D) after its main one, and a repeat of the main chart
// must still find the earlier main chart behind that companion.
const findPreviousSweep = (next: SweepChartAttachment): SweepChartAttachment | null => {
  const pending = usePrimitiveStore.getState().agentChatAttachments;
  for (let i = pending.length - 1; i >= 0; i--) {
    const a = pending[i];
    if (a.kind === 'sweep' && canOverlay(a as SweepChartAttachment, next)) return a as SweepChartAttachment;
  }
  const messages = useStore.getState().projectState.chatMessages ?? [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const atts = messages[i]?.attachments;
    if (!atts) continue;
    for (let j = atts.length - 1; j >= 0; j--) {
      const a = atts[j];
      if (a.kind === 'sweep' && canOverlay(a as SweepChartAttachment, next)) return a as SweepChartAttachment;
    }
  }
  return null;
};

// Append an artifact produced by a tool this turn (e.g. computeUVVisSpectrum).
//
// A SWEEP additionally absorbs the previous run of the SAME sweep as an extra
// series, so "repeat it at 600 K" draws both curves on one chart instead of
// leaving the user to compare two independently-scaled pictures. overlaySweeps
// decides whether the two runs actually belong together (same kind, same axes,
// not a categorical bar series) and returns the attachment untouched otherwise.
//
// Returns the object actually stored (the overlay may have produced a new one),
// which is the handle a LIVE sweep passes to updateAgentChatAttachment.
export const pushAgentChatAttachment = <T extends ChatAttachment>(attachment: T): T => {
  const toPush = (
    attachment.kind === 'sweep'
      ? overlaySweeps(findPreviousSweep(attachment as SweepChartAttachment), attachment as SweepChartAttachment)
      : attachment
  ) as T;
  usePrimitiveStore.getState().set((s) => {
    s.agentChatAttachments = [...s.agentChatAttachments, toPush];
  });
  return toPush;
};

// Replace an attachment pushed earlier THIS run with an updated copy, so a long
// sweep can chart its points as they arrive instead of only at the end. The
// in-progress chat panel renders the accumulator live; at turn end the final
// object is what gets captured onto the message. Matched by identity, so the
// caller keeps the returned object as the handle for the next update. A no-op
// (returning `patch` merged anyway) when the run was cleared underneath us —
// the sweep then simply has nothing visible to update.
export const updateAgentChatAttachment = <T extends ChatAttachment>(prev: T, patch: Partial<T>): T => {
  const next = { ...prev, ...patch } as T;
  // Locate against the committed state, not the immer draft: elements read
  // through the draft are proxies, so an identity search there never matches.
  const list = usePrimitiveStore.getState().agentChatAttachments;
  const i = list.indexOf(prev);
  if (i >= 0) {
    const replaced = list.slice();
    replaced[i] = next;
    usePrimitiveStore.getState().set((s) => {
      s.agentChatAttachments = replaced;
    });
  }
  return next;
};

// Called by the chat hook right BEFORE starting an agent run, so artifacts from
// a previous turn can't attach to this one.
export const clearAgentChatAttachments = (): void => {
  usePrimitiveStore.getState().set((s) => {
    s.agentChatAttachments = [];
  });
};

// Called right AFTER an agent run to spread onto the chat message. Returns an
// empty object (no `attachments` key) when nothing was produced, so ordinary
// turns stay clean.
export const captureAgentChatAttachments = (): { attachments?: ChatAttachment[] } => {
  const list = usePrimitiveStore.getState().agentChatAttachments;
  return list.length ? { attachments: list } : {};
};
